/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState, useCallback } from 'react'

import { FiSettings, FiSave, FiRefreshCw, FiGrid, FiEye, FiClock, FiLayout } from 'react-icons/fi'

import PageHeader from '@/components/ui/page-header/PageHeader'
import PageMenu, { MenuAction } from '@/components/ui/page-menu/PageMenu'
import { useDashboard } from '@/hooks'
import useLocalStorage from '@/hooks/useLocalStorage'

interface DashboardGlobalSettings {
  appearance: {
    theme: 'light' | 'dark' | 'auto'
    showGridLines: boolean
    gridSize: number
    showWidgetHeaders: boolean
    compactMode: boolean
  }
  behavior: {
    autoSave: boolean
    autoRefresh: boolean
    refreshInterval: number // in seconds
    confirmBeforeDelete: boolean
    enableAnimations: boolean
  }
  widgets: {
    defaultRefreshInterval: number // in seconds
    showTooltips: boolean
    enableDragDrop: boolean
    maxWidgetsPerDashboard: number
  }
  layout: {
    defaultGridCols: number
    snapToGrid: boolean
    showLayoutGuides: boolean
    enableResponsiveLayouts: boolean
  }
}

/**
 * Dashboard Settings page for configuring global dashboard preferences
 */
const DashboardSettings: React.FC = () => {
  const dashboardContext = useDashboard()
  const defaultSettings: DashboardGlobalSettings = {
    appearance: {
      theme: 'auto',
      showGridLines: true,
      gridSize: 12,
      showWidgetHeaders: true,
      compactMode: false,
    },
    behavior: {
      autoSave: true,
      autoRefresh: true,
      refreshInterval: 30,
      confirmBeforeDelete: true,
      enableAnimations: true,
    },
    widgets: {
      defaultRefreshInterval: 30,
      showTooltips: true,
      enableDragDrop: true,
      maxWidgetsPerDashboard: 20,
    },
    layout: {
      defaultGridCols: 12,
      snapToGrid: true,
      showLayoutGuides: true,
      enableResponsiveLayouts: true,
    },
  }

  const [settings, setSettings] = useLocalStorage<DashboardGlobalSettings>('dashboardGlobalSettings', defaultSettings)
  const [activeTab, setActiveTab] = useState('appearance')
  const [hasChanges, setHasChanges] = useState(false)

  const updateSettings = useCallback(
    (section: keyof DashboardGlobalSettings, field: string, value: unknown) => {
      setSettings((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }))
      setHasChanges(true)
    },
    [setSettings]
  )

  const handleSave = useCallback(() => {
    // In a real app, this could also save to the server
    setHasChanges(false)

    // Apply some settings globally (like grid lines)
    if (typeof window !== 'undefined') {
      document.documentElement.setAttribute('data-dashboard-theme', settings.appearance.theme)
    }
  }, [settings])

  const handleReset = useCallback(() => {
    setSettings(defaultSettings)
    setHasChanges(false)
  }, [setSettings, defaultSettings])

  const tabs = [
    { id: 'appearance', label: 'Appearance', icon: FiEye },
    { id: 'behavior', label: 'Behavior', icon: FiClock },
    { id: 'widgets', label: 'Widgets', icon: FiGrid },
    { id: 'layout', label: 'Layout', icon: FiLayout },
  ]

  const menuActions: MenuAction[] = [
    {
      id: 'save',
      label: 'Save Settings',
      icon: <FiSave />,
      onClick: handleSave,
      variant: 'primary',
      disabled: !hasChanges,
    },
    {
      id: 'reset',
      label: 'Reset to Defaults',
      icon: <FiRefreshCw />,
      onClick: handleReset,
      variant: 'outline',
    },
  ]

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Theme</span>
          </label>
          <select
            className="select select-bordered"
            value={settings.appearance.theme}
            onChange={(e) => updateSettings('appearance', 'theme', e.target.value)}
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto (System)</option>
          </select>
          <label className="label">
            <span className="label-text-alt">Automatically switch based on system preference</span>
          </label>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">Grid Size</span>
          </label>
          <select
            className="select select-bordered"
            value={settings.appearance.gridSize}
            onChange={(e) => updateSettings('appearance', 'gridSize', parseInt(e.target.value))}
          >
            <option value="8">8 Columns</option>
            <option value="12">12 Columns</option>
            <option value="16">16 Columns</option>
            <option value="24">24 Columns</option>
          </select>
          <label className="label">
            <span className="label-text-alt">Number of columns in the dashboard grid</span>
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Show Grid Lines</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.appearance.showGridLines}
              onChange={(e) => updateSettings('appearance', 'showGridLines', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Display grid lines to help with widget alignment</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Show Widget Headers</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.appearance.showWidgetHeaders}
              onChange={(e) => updateSettings('appearance', 'showWidgetHeaders', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Display headers on all widgets by default</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Compact Mode</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.appearance.compactMode}
              onChange={(e) => updateSettings('appearance', 'compactMode', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Reduce spacing and padding for more content</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderBehaviorSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Auto Save</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.behavior.autoSave}
              onChange={(e) => updateSettings('behavior', 'autoSave', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Automatically save dashboard changes</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Auto Refresh</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.behavior.autoRefresh}
              onChange={(e) => updateSettings('behavior', 'autoRefresh', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Automatically refresh dashboard data</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">Refresh Interval (seconds)</span>
          </label>
          <input
            type="number"
            className="input input-bordered"
            value={settings.behavior.refreshInterval}
            onChange={(e) => updateSettings('behavior', 'refreshInterval', parseInt(e.target.value))}
            min="10"
            max="300"
            disabled={!settings.behavior.autoRefresh}
          />
          <label className="label">
            <span className="label-text-alt">How often to refresh dashboard data</span>
          </label>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Confirm Before Delete</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.behavior.confirmBeforeDelete}
              onChange={(e) => updateSettings('behavior', 'confirmBeforeDelete', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Show confirmation dialog before deleting widgets or layouts</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Enable Animations</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.behavior.enableAnimations}
              onChange={(e) => updateSettings('behavior', 'enableAnimations', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Show smooth transitions and animations</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderWidgetSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Default Refresh Interval (seconds)</span>
          </label>
          <input
            type="number"
            className="input input-bordered"
            value={settings.widgets.defaultRefreshInterval}
            onChange={(e) => updateSettings('widgets', 'defaultRefreshInterval', parseInt(e.target.value))}
            min="10"
            max="300"
          />
          <label className="label">
            <span className="label-text-alt">Default refresh rate for new widgets</span>
          </label>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">Max Widgets Per Dashboard</span>
          </label>
          <input
            type="number"
            className="input input-bordered"
            value={settings.widgets.maxWidgetsPerDashboard}
            onChange={(e) => updateSettings('widgets', 'maxWidgetsPerDashboard', parseInt(e.target.value))}
            min="5"
            max="50"
          />
          <label className="label">
            <span className="label-text-alt">Maximum number of widgets allowed on a single dashboard</span>
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Show Tooltips</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.widgets.showTooltips}
              onChange={(e) => updateSettings('widgets', 'showTooltips', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Show helpful tooltips on widget hover</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Enable Drag & Drop</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.widgets.enableDragDrop}
              onChange={(e) => updateSettings('widgets', 'enableDragDrop', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Allow widgets to be repositioned by dragging</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderLayoutSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Default Grid Columns</span>
          </label>
          <select
            className="select select-bordered"
            value={settings.layout.defaultGridCols}
            onChange={(e) => updateSettings('layout', 'defaultGridCols', parseInt(e.target.value))}
          >
            <option value="8">8 Columns</option>
            <option value="12">12 Columns</option>
            <option value="16">16 Columns</option>
            <option value="24">24 Columns</option>
          </select>
          <label className="label">
            <span className="label-text-alt">Default number of columns for new dashboards</span>
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Snap to Grid</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.layout.snapToGrid}
              onChange={(e) => updateSettings('layout', 'snapToGrid', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Automatically align widgets to grid positions</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Show Layout Guides</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.layout.showLayoutGuides}
              onChange={(e) => updateSettings('layout', 'showLayoutGuides', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Display alignment guides when dragging widgets</span>
          </div>
        </div>

        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text">Enable Responsive Layouts</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={settings.layout.enableResponsiveLayouts}
              onChange={(e) => updateSettings('layout', 'enableResponsiveLayouts', e.target.checked)}
            />
          </label>
          <div className="label">
            <span className="label-text-alt">Automatically adjust layout for different screen sizes</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'appearance':
        return renderAppearanceSettings()
      case 'behavior':
        return renderBehaviorSettings()
      case 'widgets':
        return renderWidgetSettings()
      case 'layout':
        return renderLayoutSettings()
      default:
        return null
    }
  }

  // Calculate some dashboard statistics
  const totalWidgets = (dashboardContext.configuration?.dashboards || []).reduce(
    (acc, layout) => acc + layout.insights.length,
    0
  )
  const totalLayouts = dashboardContext.configuration?.dashboards?.length || 0

  return (
    <div className="p-4 space-y-6">
      <PageHeader
        title="Dashboard Settings"
        subtitle="Configure global dashboard preferences and behavior"
        icon={<FiSettings />}
        menu={<PageMenu actions={menuActions} />}
      />

      {hasChanges && (
        <div className="alert alert-warning">
          <FiSave className="w-6 h-6" />
          <span>You have unsaved changes. Don&apos;t forget to save before leaving this page.</span>
        </div>
      )}

      {/* Dashboard Statistics */}
      <div className="stats stats-horizontal shadow bg-base-100 w-full">
        <div className="stat">
          <div className="stat-figure text-primary">
            <FiLayout className="w-8 h-8" />
          </div>
          <div className="stat-title">Total Dashboards</div>
          <div className="stat-value text-primary">{totalLayouts}</div>
          <div className="stat-desc">Saved layouts</div>
        </div>

        <div className="stat">
          <div className="stat-figure text-secondary">
            <FiGrid className="w-8 h-8" />
          </div>
          <div className="stat-title">Total Widgets</div>
          <div className="stat-value text-secondary">{totalWidgets}</div>
          <div className="stat-desc">Across all dashboards</div>
        </div>

        <div className="stat">
          <div className="stat-figure text-accent">
            <FiClock className="w-8 h-8" />
          </div>
          <div className="stat-title">Refresh Rate</div>
          <div className="stat-value text-accent">{settings.behavior.refreshInterval}s</div>
          <div className="stat-desc">Auto refresh interval</div>
        </div>

        <div className="stat">
          <div className="stat-figure text-info">
            <FiEye className="w-8 h-8" />
          </div>
          <div className="stat-title">Grid Lines</div>
          <div className="stat-value text-info">{settings.appearance.showGridLines ? 'ON' : 'OFF'}</div>
          <div className="stat-desc">Visual assistance</div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body p-0">
          {/* Tabs */}
          <div className="tabs tabs-boxed p-4 pb-0">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  className={`tab tab-lg gap-2 ${activeTab === tab.id ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>

      {/* Settings Info Card */}
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg">Settings Information</h3>
          <div className="space-y-2 text-sm">
            <p>
              <strong>Storage:</strong> Settings are saved to your browser&apos;s local storage and will persist across
              sessions.
            </p>
            <p>
              <strong>Scope:</strong> These settings apply to all dashboards and will affect the default behavior for
              new widgets and layouts.
            </p>
            <p>
              <strong>Reset:</strong> Use the &quot;Reset to Defaults&quot; button to restore all settings to their
              original values.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardSettings
